import clsx from "clsx";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { Seomatic } from "react-seomatic";

import BodyContent from "@components/BodyContent";
import ColumnLayout from "@components/ColumnLayout";
import DotBorder from "@components/DotBorder";
import Heading from "@components/Heading";
import { ArrowLeft, ArrowRight } from "@components/Icons";
import SidebarCallToAction from "@components/SidebarCallToAction";
import Target from "@components/Target";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import StatusBarDots from "@images/StatusBar-Dots.svg";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { getAttackCategoryArray } from "@lib/getAttackCategoryArray";
import getImageBlurOptions from "@lib/getImageBlurOptions";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import QueryAttackLibrary from "@queries/attack-library/QueryAttackLibrary.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";
import dayjs from "dayjs";
import parse from "html-react-parser";
import sample from "lodash/sample";
import Image from 'next/image';
import Link from "next/link";

export const getStaticPaths: GetStaticPaths = async (context) => {
	// Fetch every possible path

	const response = await craft(QuerySlugs, {
		section: "attackLibrary",
		limit: null,
	});

	const paths = [];

	response.entries.map((attack) => {
		console.log(`Generating /attack-library/${attack.slug}`);

		paths.push({
			params: { slug: attack.slug },
		});
	});

	return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview,
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;

	// Construct uri from the slug array
	const { slug } = params;

	// What is the uri
	const uri = `/attack-library/${slug}`;

	// Fetch the page's data
	const response = await craft(
		QueryAttackLibrary,
		{ slug },
		preview ? previewData.token : null
	);

	const {
		attack,
		primaryNavigation,
		primaryCallToActions,
		globalSet,

		blogIndex,
	} = response;

	// Return props if entry exists, otherwise check Retour for redirects or 404
	const returnResponse = await returnStaticProps(
		{
			navigation: {
				primaryNavigation,
				primaryCallToActions,
			},
			globalSet,
			attack,
			blogIndex,
		},
		!attack,
		uri
	);

	return returnResponse;
};

interface IProps {
	attack: any;
	blogIndex: any;
}

export default function Page({
	attack,
	blogIndex,
}: IProps): JSX.Element {
	useSetHeaderTheme("dark");
	const parsedDate = dayjs(attack.attackDate);
	const [attackCategories] = getAttackCategoryArray(attack);

	const sidebarCallToAction = sample(blogIndex.callToActions);

	return (
		<>
			<Seomatic Head={Head} {...attack.seomatic} />
			<div
				className={clsx(
					"Attack__Title-container mx-auto p-7.5 md:p-15 relative"
				)}
			>
				<div
					className={clsx(
						"bg-white-01 pt-5 md:pt-10 pb-5 md:pb-10 p-5 md:px-15  max-w-screen-xl mx-auto  box-sizing relative"
					)}
				>
					<Link
						href="/attack-library"
						className="Attack__return font-medium inline-block border-b-[3px] border-purple-01 pb-1 mb-3"
					>
						Back to All Attack Library
					</Link>

					<h1 className={clsx("text-2xl md:text-3xl font-medium")}>
						{attack.title}
					</h1>

					{attack.summary && (
						<div className={clsx("PageTitle__summary", "text-purple-01 mt-4")}>
							<div
								className={clsx(
									"PageTitle__summary-text text-lg md:text-2xl max-w-3xl"
								)}
							>
								{attack.summary}
							</div>
						</div>
					)}

					<DotBorder />
				</div>
			</div>
			<div className={clsx("relative bg-white-01")}>
				<DotBorder topOnly />
				<div
					className={clsx("PostContent relative py-7.5 md:py-15 px-3 md:px-5")}
				>
					<ColumnLayout>
						<ColumnLayout.MainColumn>
							<BodyContent sections={attack.bodyContent.nodes} />
							<div className="Attack__PrevNext-container flex mt-5">
								<div className="w-full lg:w-1/2">
									{attack.prev && (
										<Link
											href={attack.prev?.url}
											className="py-5 block flex items-center text-sm"
										>
											<span className="mr-7.5">
												<ArrowLeft />
											</span>
											<span>Previous Attack</span>
										</Link>
									)}
								</div>

								<div className="w-full lg:w-1/2 text-right">
									{attack.next && (
										<Link
											href={attack.next?.url}
											className="py-5 block flex text-sm items-center justify-end"
										>
											<span>Next Attack</span>
											<span className="ml-7.5">
												<ArrowRight />
											</span>
										</Link>
									)}
								</div>
							</div>
						</ColumnLayout.MainColumn>
						<ColumnLayout.SidebarColumn>
							<div className={clsx("bg-yellow-01 p-7.5 pb-3 mb-7.5")}>
								<Heading size="h3" className="mb-10">
									Analysis Overview
								</Heading>
								{Object.keys(attackCategories).map((group, index) => {
									return (
										<div key={index} className="Attack__category mb-7.5">
											<h3 className="text-sm font-medium pb-1 mb-3 border-b">
												{group === "attackTypes" && "Type"}
												{group === "attackTactics" && "Tactic"}
												{group === "attackGoals" && "Goal"}
												{group === "attackVectors" && "Vector"}
												{group === "attackThemes" && "Theme"}
												{group === "attackImpersonatedParties" &&
													"Impersonated Party"}
												{group === "attackImpersonatedBrands" &&
													"Impersonated Brands"}
												{group === "attackLanguage" && "Language"}
												{group === "attackAttachmentType" && "Attachment Type"}
												{group === "attackAiGenerated" && "AI Generated"}
											</h3>
											{attackCategories[group].map((category, index) => {
												return (
													<div
														key={index}
														className="text-base font-medium mb-1"
                          >
                            {category.title}
													</div>
												);
											})}
										</div>
									);
								})}
							</div>
							{!!sidebarCallToAction && (
								<SidebarCallToAction
									heading={sidebarCallToAction.heading}
									image={sidebarCallToAction.image[0]}
									imageBlur={sidebarCallToAction.imageBlur[0]}
									target={sidebarCallToAction.target}
								/>
							)}
						</ColumnLayout.SidebarColumn>
					</ColumnLayout>
				</div>
			</div>
		</>
	);
}
